import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  MessageSquare,
  Send,
  User,
  Loader2,
  Lock,
  CheckCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatWidget = () => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") || "nickname";
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [supportAdmin, setSupportAdmin] = useState(null);
  
  // Auth state
  const [nickname, setNickname] = useState("");
  const [pin, setPin] = useState("");
  const [nicknameStatus, setNicknameStatus] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [checkingNickname, setCheckingNickname] = useState(false);
  const [guestName, setGuestName] = useState("");
  
  const ws = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    initWidget();
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Check nickname availability
  useEffect(() => {
    if (nickname.length < 2) {
      setNicknameStatus(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingNickname(true);
      try {
        const response = await fetch(`${API}/auth/check-nickname/${encodeURIComponent(nickname)}`);
        const data = await response.json();
        setNicknameStatus(data);
      } catch (error) {
        console.error("Error checking nickname:", error);
      } finally {
        setCheckingNickname(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [nickname]);

  const initWidget = async () => {
    try {
      if (mode === "anonymous") {
        // Anonymous mode - create guest session
        const response = await fetch(`${API}/widget/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ mode: "anonymous" }),
        });
        
        if (response.ok) {
          const data = await response.json();
          setSession(data);
        }
      } else if (mode === "support") {
        // Support mode - fetch available admins
        try {
          const response = await fetch(`${API}/widget/support-admins`);
          if (response.ok) {
            const admins = await response.json();
            if (admins.length > 0) {
              setSupportAdmin(admins[0]); // Get first available admin
            }
          }
        } catch (error) {
          console.error("Error fetching support admins:", error);
        }
      }
      // For nickname mode, user must authenticate
    } catch (error) {
      console.error("Error initializing widget:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async () => {
    if (!nickname || nickname.length < 2) {
      toast.error("Ingresa un nickname válido");
      return;
    }
    if (!pin || pin.length !== 4) {
      toast.error("Ingresa un PIN de 4 dígitos");
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API}/auth/nickname`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ nickname, pin }),
      });
      
      if (response.ok) {
        const userData = await response.json();
        setSession({ user: userData });
        connectWebSocket(userData.user_id);
        fetchConversations();
        fetchUsers();
        toast.success(`¡Bienvenido, ${userData.name}!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al iniciar sesión");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSupportStart = async () => {
    if (!guestName || guestName.length < 2) {
      toast.error("Ingresa tu nombre");
      return;
    }
    
    setAuthLoading(true);
    try {
      // Create a guest session for support
      const response = await fetch(`${API}/widget/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          mode: "support",
          guest_name: guestName
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSession(data);
        
        // If we have support admin info, start conversation with them
        if (supportAdmin) {
          toast.success("Conectando con soporte...");
          // Create a conversation with an admin
          const convResponse = await fetch(`${API}/widget/support-conversation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              guest_id: data.user.user_id,
              guest_name: guestName,
              admin_id: supportAdmin.user_id
            }),
          });
          
          if (convResponse.ok) {
            const convData = await convResponse.json();
            setSelectedConversation(convData);
            fetchMessages(convData.conversation_id);
            connectWebSocket(data.user.user_id);
          }
        }
      } else {
        toast.error("Error al iniciar sesión");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setAuthLoading(false);
    }
  };

  const connectWebSocket = (userId) => {
    const wsUrl = BACKEND_URL.replace(/^http/, 'ws');
    const socket = new WebSocket(`${wsUrl}/api/ws/${userId}`);
    
    socket.onopen = () => {
      // Auth por primer mensaje (token nunca en URL para evitar leaks en logs/historial/Referer)
      const token = localStorage.getItem('session_token') || '';
      try { socket.send(JSON.stringify({ type: "auth", token })); } catch (e) {}
      console.log("Widget WebSocket connected, auth sent");
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "new_message" && selectedConversation) {
        if (data.message.conversation_id === selectedConversation.conversation_id) {
          setMessages(prev => {
            if (prev.some(m => m.message_id === data.message.message_id)) return prev;
            return [...prev, data.message];
          });
        }
      }
    };
    
    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    
    ws.current = socket;
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/conversations`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.filter(u => u.user_id !== session?.user?.user_id));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API}/messages/${conversationId}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.conversation_id);
  };

  const startConversation = async (userId) => {
    try {
      const response = await fetch(`${API}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ participant_ids: [userId], is_group: false }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data);
        fetchMessages(data.conversation_id);
        fetchConversations();
      }
    } catch (error) {
      toast.error("Error al crear conversación");
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          conversation_id: selectedConversation.conversation_id,
          content: messageInput,
        }),
      });
      
      if (response.ok) {
        const newMsg = await response.json();
        setMessages(prev => [...prev, newMsg]);
        setMessageInput("");
      }
    } catch (error) {
      toast.error("Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full absolute inset-0 flex items-center justify-center bg-[#17212b]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  // Login screen for nickname mode
  if (mode === "nickname" && !session) {
    return (
      <div className="w-full absolute inset-0 bg-[#17212b] flex flex-col overflow-auto">
        {/* Header */}
        <div className="h-16 bg-[#232e3c] flex items-center justify-center px-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-white">OkChat</span>
          </div>
        </div>

        {/* Login Form */}
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Bienvenido</h2>
              <p className="text-slate-400">Ingresa para comenzar a chatear</p>
            </div>
            
            <div className="space-y-4">
              {/* Nickname Input */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Nickname</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    placeholder="Tu nickname"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-[#242f3d] border-slate-600 text-white placeholder:text-slate-500"
                    maxLength={20}
                    disabled={authLoading}
                  />
                  {checkingNickname && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 animate-spin" />
                  )}
                  {!checkingNickname && nicknameStatus && nickname.length >= 2 && (
                    <CheckCircle className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
                      nicknameStatus.available || nicknameStatus.can_login ? 'text-emerald-500' : 'text-red-500'
                    }`} />
                  )}
                </div>
                {nicknameStatus && nickname.length >= 2 && (
                  <p className={`text-xs ${
                    nicknameStatus.available ? 'text-emerald-400' : 
                    nicknameStatus.can_login ? 'text-blue-400' : 'text-red-400'
                  }`}>
                    {nicknameStatus.can_login ? "Ingresa tu PIN" : 
                     nicknameStatus.available ? "Crea un PIN de 4 dígitos" : nicknameStatus.message}
                  </p>
                )}
              </div>
              
              {/* PIN Input with Numeric Keypad */}
              {(nicknameStatus?.available || nicknameStatus?.can_login) && (
                <div className="space-y-2">
                  <label className="text-sm text-slate-400 text-center block">
                    {nicknameStatus?.available ? "Crear PIN (4 dígitos)" : "PIN de acceso"}
                  </label>
                  
                  {/* PIN Display Dots */}
                  <div className="flex justify-center gap-3 mb-4">
                    {[0, 1, 2, 3].map((i) => (
                      <div 
                        key={i} 
                        className={`w-4 h-4 rounded-full transition-all duration-200 ${
                          i < pin.length ? 'bg-red-500 scale-110' : 'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Numeric Keypad */}
                  <div className="grid grid-cols-3 gap-2 max-w-[220px] mx-auto">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => {
                          if (pin.length < 4) {
                            setPin(pin + num);
                          }
                        }}
                        disabled={authLoading}
                        className="h-12 rounded-lg bg-[#242f3d] hover:bg-slate-600 active:bg-red-600 text-white text-xl font-medium transition-all duration-150 border border-slate-600 disabled:opacity-50"
                      >
                        {num}
                      </button>
                    ))}
                    <div></div>
                    <button
                      type="button"
                      onClick={() => {
                        if (pin.length < 4) {
                          setPin(pin + '0');
                        }
                      }}
                      disabled={authLoading}
                      className="h-12 rounded-lg bg-[#242f3d] hover:bg-slate-600 active:bg-red-600 text-white text-xl font-medium transition-all duration-150 border border-slate-600 disabled:opacity-50"
                    >
                      0
                    </button>
                    <button
                      type="button"
                      onClick={() => setPin(pin.slice(0, -1))}
                      disabled={authLoading}
                      className="h-12 rounded-lg bg-[#242f3d] hover:bg-slate-600 active:bg-red-600 text-white text-lg font-medium transition-all duration-150 border border-slate-600 disabled:opacity-50 flex items-center justify-center"
                    >
                      ⌫
                    </button>
                  </div>
                  
                  <p className="text-center text-slate-500 text-xs mt-2">
                    {nicknameStatus?.available 
                      ? "Crea tu PIN de 4 dígitos" 
                      : "Ingresa tu PIN de 4 dígitos"
                    }
                  </p>
                </div>
              )}
              
              {/* Login Button */}
              <Button
                onClick={handleAuth}
                disabled={authLoading || nickname.length < 2 || pin.length !== 4}
                className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-lg"
              >
                {authLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Ingresando...
                  </>
                ) : (
                  nicknameStatus?.can_login ? "Ingresar" : "Crear cuenta"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login screen for support mode
  if (mode === "support" && !session) {
    return (
      <div className="w-full absolute inset-0 bg-[#17212b] flex flex-col overflow-auto">
        {/* Header */}
        <div className="h-16 bg-[#232e3c] flex items-center justify-center px-4 border-b border-slate-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-white">Soporte</span>
          </div>
        </div>

        {/* Support Form */}
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-full max-w-sm space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-emerald-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-10 h-10 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">¿Necesitas ayuda?</h2>
              <p className="text-slate-400">Ingresa tu nombre para contactar con soporte</p>
              {supportAdmin && (
                <p className="text-xs text-emerald-400 mt-2">
                  Un agente está disponible para ayudarte
                </p>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Name Input */}
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Tu nombre</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    placeholder="Escribe tu nombre"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSupportStart()}
                    className="pl-10 h-12 bg-[#242f3d] border-slate-600 text-white placeholder:text-slate-500"
                    maxLength={50}
                    disabled={authLoading}
                  />
                </div>
              </div>
              
              {/* Start Button */}
              <Button
                onClick={handleSupportStart}
                disabled={authLoading || guestName.length < 2}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-lg"
              >
                {authLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  "Iniciar Chat de Soporte"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Chat interface
  return (
    <div className="w-full bg-[#17212b] flex flex-col absolute inset-0">
      {/* Header */}
      <div className="h-14 bg-[#232e3c] flex items-center justify-between px-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center space-x-3">
          {selectedConversation && (
            <button
              onClick={() => setSelectedConversation(null)}
              className="text-slate-400 hover:text-white mr-2"
            >
              ←
            </button>
          )}
          <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">
              {selectedConversation?.name || session?.user?.name || "OkChat"}
            </h3>
            <p className="text-xs text-slate-400">
              {session?.user?.is_guest ? "Invitado" : "En línea"}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {!selectedConversation ? (
          // Conversation list or user list
          <ScrollArea className="h-full">
            <div className="p-4">
              {conversations.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Conversaciones</p>
                  {conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      onClick={() => selectConversation(conv)}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#232e3c] transition-colors"
                    >
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-red-600 text-white">
                          {(conv.name || "C").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{conv.name || "Chat"}</p>
                        <p className="text-xs text-slate-400 truncate">{conv.last_message?.content || "Sin mensajes"}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Usuarios disponibles</p>
                  {users.length > 0 ? (
                    users.slice(0, 10).map((u) => (
                      <div
                        key={u.user_id}
                        onClick={() => startConversation(u.user_id)}
                        className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-[#232e3c] transition-colors"
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={u.picture ? `${BACKEND_URL}${u.picture}` : null} />
                          <AvatarFallback className="bg-red-600 text-white">
                            {u.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{u.name}</p>
                          <p className="text-xs text-slate-400">@{u.nickname}</p>
                        </div>
                        {u.status === 'online' && (
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No hay usuarios disponibles</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        ) : (
          // Messages view
          <div className="h-full flex flex-col">
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay mensajes aún</p>
                    <p className="text-xs mt-1">¡Envía el primero!</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.sender_id === session?.user?.user_id;
                    return (
                      <div
                        key={msg.message_id}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            isOwn
                              ? 'bg-red-600 text-white rounded-br-md'
                              : 'bg-[#232e3c] text-white rounded-bl-md'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${isOwn ? 'text-red-200' : 'text-slate-500'}`}>
                            {new Date(msg.created_at).toLocaleTimeString('es-MX', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-3 bg-[#232e3c] border-t border-slate-700">
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Escribe un mensaje..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 bg-[#17212b] border-slate-600 text-white placeholder:text-slate-500"
                  disabled={sending}
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !messageInput.trim()}
                  size="icon"
                  className="bg-red-600 hover:bg-red-700 shrink-0"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
